import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { Counter, EASE, AnimatedProgress } from "../../components/motion";
import { ErrorState, MotionButton } from "../../components/common";

function Result({ r }) {
  const ok = r.passed;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }} className="surface relative mx-auto max-w-lg overflow-hidden p-8 text-center" data-testid="exam-result">
      <div className="orb left-1/2 top-0 h-60 w-60 -translate-x-1/2 -translate-y-1/2" style={{ background: ok ? "rgba(16,185,129,0.25)" : "rgba(244,63,94,0.2)" }} />
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }} className={`relative mx-auto grid h-20 w-20 place-items-center rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"} text-white shadow-lg`}>
        <svg viewBox="0 0 24 24" className="h-10 w-10" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          {ok ? <path className="draw-path" d="M5 12.5l4.5 4.5L19 7.5" /> : <path className="draw-path" d="M7 7l10 10M17 7L7 17" />}
        </svg>
      </motion.div>
      <h2 className="relative mt-6 text-2xl font-extrabold" data-testid="exam-result-status">{ok ? "Tebrikler, Başarılı!" : "Başarısız"}</h2>
      <p className="relative mt-1 text-sm text-slate-500">Geçme puanı: {r.pass_score}</p>
      <Counter value={r.score} className="relative mt-6 block text-6xl font-extrabold tracking-tight" testid="exam-result-score" />
      <div className="relative mt-6 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-emerald-50 p-3"><p className="text-xs text-emerald-700">Doğru</p><p className="text-xl font-bold text-emerald-700" data-testid="exam-result-correct">{r.correct}</p></div>
        <div className="rounded-xl bg-rose-50 p-3"><p className="text-xs text-rose-700">Yanlış</p><p className="text-xl font-bold text-rose-700" data-testid="exam-result-wrong">{r.wrong}</p></div>
      </div>
      <Link to="/panel/sinavlarim" className="btn-ghost relative mt-7 inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="exam-result-back-btn">Sınavlarıma Dön</Link>
    </motion.div>
  );
}

export default function ExamTake() {
  const { examId } = useParams();
  const { data, loading, error, reload } = useApi(`/me/exams/${examId}`);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading) return <div className="skeleton mx-auto h-96 max-w-2xl" />;
  if (result) return <Result r={result} />;
  const qs = data.questions;
  const q = qs[i];
  const go = (d) => { setDir(d); setI(i + d); };
  const submit = async () => {
    if (Object.keys(answers).length < qs.length) return toast.error("Lütfen tüm soruları cevaplayın.");
    setBusy(true);
    try {
      const r = await api.post(`/me/exams/${examId}/submit`, { answers });
      setResult(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/panel/sinavlarim" className="group mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[var(--ink)]" data-testid="exam-back-link"><ArrowLeft className="h-4 w-4" /> Sınavlarım</Link>
      <h1 className="text-2xl font-extrabold tracking-tight" data-testid="exam-title">{data.title}</h1>
      <div className="mt-4 mb-6 flex items-center gap-3">
        <AnimatedProgress value={Math.round(((i + 1) / qs.length) * 100)} />
        <span className="shrink-0 text-sm font-semibold text-slate-500" data-testid="exam-question-counter">{i + 1} / {qs.length}</span>
      </div>
      <div className="surface overflow-hidden p-6 sm:p-8">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div key={q.id} custom={dir} initial={{ opacity: 0, x: 30 * dir }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 * dir }} transition={{ duration: 0.28, ease: EASE }}>
            <h2 className="text-lg font-bold text-[var(--ink)]" data-testid="exam-question-text">{q.text}</h2>
            <div className="mt-6 space-y-3">
              {q.options.map((o, oi) => {
                const sel = answers[q.id] === oi;
                return (
                  <button key={oi} onClick={() => setAnswers({ ...answers, [q.id]: oi })} data-testid={`exam-option-${oi}`} className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left text-sm font-medium transition-[border-color,background-color,transform] duration-200 active:scale-[0.99] ${sel ? "border-[var(--brand)] bg-[var(--brand-50)] text-[var(--ink)]" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[11px] font-bold transition-colors ${sel ? "border-[var(--brand)] bg-[var(--brand)] text-white" : "border-slate-300 text-slate-400"}`}>
                      {sel ? <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}><Check className="h-3.5 w-3.5" /></motion.span> : String.fromCharCode(65 + oi)}
                    </span>
                    {o}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="mt-8 flex items-center justify-between gap-3">
          <button disabled={i === 0} onClick={() => go(-1)} className="btn-ghost inline-flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:opacity-40" data-testid="exam-prev-btn"><ArrowLeft className="h-4 w-4" /> Önceki</button>
          {i < qs.length - 1 ? (
            <button onClick={() => go(1)} className="btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="exam-next-btn">Sonraki <ArrowRight className="h-4 w-4" /></button>
          ) : (
            <MotionButton onClick={submit} loading={busy} loadingText="Değerlendiriliyor..." data-testid="exam-submit-btn">Sınavı Bitir</MotionButton>
          )}
        </div>
      </div>
      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400"><X className="h-3 w-3" /> Sınavı bitirmeden ayrılırsanız cevaplarınız kaydedilmez.</p>
    </div>
  );
}
